import { Bell, Clock, Lock, LogOut } from '../../../icons';
import { tSettings } from '../../../lib/settings-i18n';
import { PanelHead } from '../PendingRow';
import { useProfileUiStore } from '../../../store/profileUiStore';
import {
  useScreenLockStore, IDLE_MINUTE_OPTIONS, LOGOUT_MINUTE_OPTIONS, FAIL_LIMIT_OPTIONS,
} from '../../../store/screenLockStore';

/**
 * 대기시간 설정 (screen-lock — 2026-08-19) — 보안설정 > 대기시간 설정.
 *
 * 값을 고르는 화면이라 펼침 목록 대신 **한눈에 다 보이는 버튼**(기존 `.st-opt` 줄 재사용)으로
 * 뒀다 — 지금 무엇이 골라져 있는지 열어보지 않아도 안다.
 * "지금 잠그기" 는 대기 시간을 기다리지 않고 바로 덮는 길이다(단축키 Ctrl/⌘+L 과 같은 동작).
 */
export function LockTimingPanel() {
  const settings = useScreenLockStore((s) => s.settings);
  const patch = useScreenLockStore((s) => s.patchSettings);
  const lock = useScreenLockStore((s) => s.lock);
  const closeSettings = useProfileUiStore((s) => s.closeSettings);

  const disabled = !settings.enabled;

  return (
    <section className="settings-section" aria-label={tSettings('settings.lock.timing_title')}>
      <PanelHead
        title={tSettings('settings.lock.timing_title')}
        desc={tSettings('settings.lock.timing_desc')}
      />

      <div className="st-lock-field">
        <span className="st-lock-field__label">
          <b>{tSettings('settings.lock.idle')}</b>
          <span>{tSettings('settings.lock.idle_desc')}</span>
        </span>
        <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.lock.idle')}>
          {IDLE_MINUTE_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={settings.idleMinutes === m}
              className={`st-opt${settings.idleMinutes === m ? ' is-on' : ''}`}
              disabled={disabled}
              onClick={() => patch({ idleMinutes: m })}
            >
              {minuteLabel(m)}
            </button>
          ))}
        </div>
      </div>

      <div className="st-lock-field">
        <span className="st-lock-field__label">
          <b>{tSettings('settings.lock.logout')}</b>
          <span>{tSettings('settings.lock.logout_desc')}</span>
        </span>
        <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.lock.logout')}>
          {LOGOUT_MINUTE_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={settings.logoutMinutes === m}
              className={`st-opt${settings.logoutMinutes === m ? ' is-on' : ''}`}
              disabled={disabled}
              onClick={() => patch({ logoutMinutes: m })}
            >
              {m === 0 ? tSettings('settings.lock.logout_never') : minuteLabel(m)}
            </button>
          ))}
        </div>
      </div>

      {/* 비밀번호를 몇 번까지 틀려도 되는가 (2026-08-28) — 전에는 코드에 박힌 값이었다. */}
      <div className="st-lock-field">
        <span className="st-lock-field__label">
          <b>{tSettings('settings.lock.fails')}</b>
          <span>{tSettings('settings.lock.fails_desc')}</span>
        </span>
        <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.lock.fails')}>
          {FAIL_LIMIT_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={settings.maxFails === n}
              className={`st-opt${settings.maxFails === n ? ' is-on' : ''}`}
              disabled={disabled}
              onClick={() => patch({ maxFails: n })}
            >
              {tSettings('settings.lock.fails_n', { n })}
            </button>
          ))}
        </div>
      </div>

      <div className="st-rows">
        <div className="st-row">
          <span className="st-row__ico" aria-hidden><Bell size={15} /></span>
          <span className="st-row__main">
            <span className="st-row__title">{tSettings('settings.lock.prewarn')}</span>
            <span className="st-row__desc">{tSettings('settings.lock.prewarn_desc')}</span>
          </span>
          <button
            type="button"
            className={`st-switch${settings.prewarn ? ' is-on' : ''}`}
            role="switch"
            aria-checked={settings.prewarn}
            aria-label={tSettings('settings.lock.prewarn')}
            disabled={disabled}
            onClick={() => patch({ prewarn: !settings.prewarn })}
          />
        </div>

        <div className="st-row">
          <span className="st-row__ico" aria-hidden><Lock size={15} /></span>
          <span className="st-row__main">
            <span className="st-row__title">{tSettings('settings.lock.lock_now')}</span>
            <span className="st-row__desc">{tSettings('settings.lock.lock_now_desc')}</span>
          </span>
          <kbd className="st-lock-kbd">Ctrl + L</kbd>
          <button
            type="button"
            className="st-opt"
            disabled={disabled}
            onClick={() => { closeSettings(); lock(); }}
          >
            <LogOut size={13} aria-hidden />
            {tSettings('settings.lock.lock_now_btn')}
          </button>
        </div>
      </div>

      <p className="st-lock-note">
        <Clock size={13} aria-hidden />
        {tSettings('settings.lock.device_note')}
      </p>
      {disabled && <p className="st-lock-note">{tSettings('settings.lock.disabled_note')}</p>}
    </section>
  );
}

function minuteLabel(m: number): string {
  return m >= 60 && m % 60 === 0
    ? tSettings('settings.lock.hour', { n: m / 60 })
    : tSettings('settings.lock.min', { n: m });
}
